import { ArrowDownIcon, BellIcon, SingleInquiryIcon, TruckIcon } from '@synergy/theme-next';
import { Box, BoxProps, ButtonProps, Container, Stack, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, ReactElement } from 'react';
import { PoolAvatar, PoolLineClampTypography } from '@synergy/components-next';
import { PoolInstance, isInstance } from '@synergy/config';
import { UserRole } from '@synergy/models';

import { AccessControl } from '../../access-control/access-control';
import { PlaceLabel } from '../../place-label/place-label';
import { PoolAlpinShopLogo } from '../../pool-alpin-shop-logo/pool-alpin-shop-logo';
import { accountSelector, userSelector } from '../../../store/configuration-selectors';
import { useAppSelector } from '../../../../store/store';

const StyledContainer = styled(Container)(({ theme }) => ({
    ...theme.typography['subtitle2'],
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1.25),
}));

const NavigationItem = styled(Box)<BoxProps & { highlight?: boolean }>(({ theme, highlight }) => ({
    height: 48,
    display: 'flex',
    gap: theme.spacing(),
    alignItems: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
    backgroundColor: highlight ? theme.palette.primary.main : undefined,
    borderRadius: highlight ? theme.shape.borderRadius : undefined,

    '&:hover': {
        backgroundColor: highlight ? theme.palette.primary.dark : undefined,
        color: highlight ? undefined : theme.palette.grey[700],
    },
}));

export interface TopNavigationDesktopProps {
    onNewsClick: (e: MouseEvent<HTMLDivElement>) => void;
    onSingleInquiryClick: () => void;
    onAccountClick: (e: MouseEvent<HTMLDivElement>) => void;
    onSupplierDirectoryClick: () => void;
}

export const TopNavigationDesktop = (props: TopNavigationDesktopProps): ReactElement => {
    const user = useAppSelector(userSelector);
    const account = useAppSelector(accountSelector);

    return (
        <StyledContainer maxWidth="xl">
            <Stack direction="row" alignItems="center">
                <PoolAlpinShopLogo />
                <PlaceLabel />
            </Stack>
            <Stack direction="row">
                <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                    <NavigationItem onClick={props.onNewsClick}>
                        <BellIcon fontSize="medium" />
                        <FormattedMessage id="app.topNavigation.news" />
                        <ArrowDownIcon />
                    </NavigationItem>
                    <NavigationItem
                        highlight={isInstance(PoolInstance.CH)}
                        data-testid="single-inquiry-button"
                        onClick={props.onSingleInquiryClick}
                    >
                        <SingleInquiryIcon fontSize="medium" />
                        <FormattedMessage id="app.topNavigation.singleInquiry" />
                    </NavigationItem>
                    <NavigationItem onClick={props.onSupplierDirectoryClick}>
                        <TruckIcon fontSize="medium" />
                        <FormattedMessage id="app.supplierDirectory.title" />
                    </NavigationItem>
                </AccessControl>
                <NavigationItem sx={{ pr: 0 }} onClick={props.onAccountClick} data-testid="account-menu">
                    {user && <PoolAvatar firstName={user.firstName} lastName={user.lastName} />}
                    <PoolLineClampTypography variant="subtitle2" sx={{ maxWidth: 176 }}>
                        {account?.name}
                    </PoolLineClampTypography>
                    <ArrowDownIcon />
                </NavigationItem>
            </Stack>
        </StyledContainer>
    );
};
